import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {ActivityPeriodEntity, DVIREntity, getStringSorter, reportTimeType} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect, useMemo, useState} from "react";
import {ActionIcon, createStyles, Group, ScrollArea} from "@mantine/core";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {IconChevronRight, IconEye, IconFolders, IconReceipt} from "@tabler/icons-react";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedActivityPeriodDate} from "../../date/FormattedActivityPeriodDate/FormattedActivityPeriodDate";
import {FormattedActivityPeriodStatus} from "../../text/FormattedActivityPeriodStatus/FormattedActivityPeriodStatus";
import {ActivityPeriodFileTableModal} from "../../table/ActivityPeriodFileTable/ActivityPeriodFileTable";
import {useActivityPeriodDataTableFilter} from "./useActivityPeriodDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {usePagination} from "../DataTable/usePagination";
import {useNavigate} from "react-router-dom";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {sortBy} from "lodash";

const useStyles = createStyles((theme) => ({
  expandIcon: {
    transition: 'transform 0.2s ease',
  },
  expandIconRotated: {
    transform: 'rotate(90deg)',
  },
}));

const compareActivityPeriodDate = (a: ActivityPeriodEntity, b: ActivityPeriodEntity): number => {
  let aDttm: number | undefined = undefined;
  let bDttm: number | undefined = undefined;

  if (a.type === reportTimeType.daily) {
    aDttm = a.fromDttm
  } else if (a.type === reportTimeType.weekly) {
    aDttm = a.toDttm
  } else if (a.type === reportTimeType.monthly) {
    aDttm = a.toDttm
  }

  if (b.type === reportTimeType.daily) {
    bDttm = b.fromDttm
  } else if (b.type === reportTimeType.weekly) {
    bDttm = b.toDttm
  } else if (b.type === reportTimeType.monthly) {
    bDttm = b.toDttm
  }

  if (aDttm) {
    if (bDttm === undefined || aDttm > bDttm) {
      return 1;
    }
  }

  return -1;
};

const EMPTY_ARRAY: ActivityPeriodEntity[] = []

export type ActivityPeriodDataTableProps = EntityDataTableProps<ActivityPeriodEntity>

export const ActivityPeriodDataTable = (props: ActivityPeriodDataTableProps) => {
  const {
    records: activityPeriods = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const [activityPeriodLookup, setActivityPeriodLookup] = useState<Record<string, ActivityPeriodEntity[]>>({})
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [selectedActivityPeriod, setSelectedActivityPeriod] = useState<ActivityPeriodEntity | undefined>(undefined)

  const {filteredRecords, filterConfig} = useActivityPeriodDataTableFilter({
    records: activityPeriods
  })

  const sortConfig = useMemo(() => {
    return {
      "fromDttm": {
        sort: compareActivityPeriodDate,
      }
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'fromDttm', direction: 'desc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  useEffect(() => {
    const activityPeriodLookup: Record<string, ActivityPeriodEntity[]> = {}

    activityPeriods.forEach((ap) => {
      if (ap.activityPeriods) {
        if (ap.id in activityPeriodLookup) {
          activityPeriodLookup[ap.id].push(...ap.activityPeriods)
        } else {
          activityPeriodLookup[ap.id] = [...ap.activityPeriods]
        }
      }
    });

    setActivityPeriodLookup(activityPeriodLookup);
  }, [activityPeriods]);

  const onViewActionClick = useCallback((record: ActivityPeriodEntity) => {
    navigate(`/activity-period/profile?id=${record.id}`)
  }, [navigate])

  const onViewTicketsActionClick = useCallback((record: ActivityPeriodEntity) => {
    navigate(`/activity-period/profile/tickets`, { state: { id: record.id }})
  }, [navigate])

  const {cx, classes} = useStyles();

  return (
    <>
      <DataTable
        {...rest}
        {...(pagination ?? {})}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        idAccessor={"id"}
        highlightOnHover={true}
        columns={[
          {
            ...getColumnFilterProps(filterConfig, "period"),
            title: t("table.entity.activityperiod.column.type", "Period"),
            accessor: "type",
            width: 100,
            sortable: true,
            render: (record) => (
              <Group spacing="xs">
                <IconChevronRight
                  size="0.9em"
                  className={cx(classes.expandIcon, {
                    [classes.expandIconRotated]: expandedIds.includes(record.id),
                  })}
                />
                <FormattedDatabaseCode value={record.type}/>
              </Group>
            ),
          },
          {
            title: t("table.entity.activityperiod.column.date", "Date"),
            accessor: "fromDttm",
            width: 100,
            sortable: true,
            render: ({type, fromDttm, toDttm}) => (
              <FormattedActivityPeriodDate reportTimeType={type} fromDate={fromDttm} toDate={toDttm}/>
            ),
          },
          {
            ...getColumnFilterProps(filterConfig, "category"),
            title: t("table.entity.activityperiod.column.category", "Category"),
            accessor: "category.code",
            width: 100,
            sortable: true,
            render: ({category}) => (
              <FormattedDatabaseCode value={category}/>
            ),
          },
          {
            ...getColumnFilterProps(filterConfig, "operatorUnit"),
            title: t("table.entity.activityperiod.column.operatorunit", "Unit"),
            accessor: "operatorUnitId",
            width: 100,
            sortable: true,
            render: ({operatorUnitId, operatorUnitName}) => (
              operatorUnitId ? <DefaultText value={operatorUnitName}/> : undefined
            ),
          },
          {
            ...getColumnFilterProps(filterConfig, "status"),
            title: t("table.entity.activityperiod.column.status", "Status"),
            accessor: "lowestStatus.code",
            width: 100,
            sortable: true,
            render: ({lowestStatus}) => (
              <FormattedActivityPeriodStatus value={lowestStatus}/>
            ),
          },
          {
            title: t("table.entity.activityperiod.column.cost", "Revenue"),
            accessor: "cost",
            width: 100,
            sortable: true,
            render: ({totalCost}) => (
              <FormattedCurrency value={totalCost}/>
            ),
          },
          {
            title: t("table.entity.activityperiod.column.quantity", "Volume"),
            accessor: "qty",
            width: 100,
            sortable: true,
            render: ({totalQty}) => (
              <FormattedQuantity value={{number: totalQty}}/>
            ),
          },
          {
            title: t("table.entity.activityperiod.column.tickets", "Tickets"),
            accessor: "ticketQty",
            width: 100,
            sortable: true,
            render: ({totalTicketQty}) => (
              <FormattedQuantity value={{number: totalTicketQty}} options={{maximumFractionDigits: 0}}/>
            ),
          },
          {
            title: "",
            accessor: "",
            width: 100,
            sortable: true,
            render: (record) => (
              <Group spacing={4} position="right" noWrap>
                <ActionIcon onClick={(e) => {
                  e.stopPropagation();
                  onViewTicketsActionClick(record)
                }}>
                  <IconReceipt size={16}/>
                </ActionIcon>
                <ActionIcon onClick={(e) => {
                  e.stopPropagation();
                  setSelectedActivityPeriod(record)
                }}>
                  <IconFolders size={16}/>
                </ActionIcon>
                <ActionIcon onClick={e => {
                  e.stopPropagation();
                  onViewActionClick(record)
                }}>
                  <IconEye size={16}/>
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        rowExpansion={{
          allowMultiple: true,
          expanded: {
            recordIds: expandedIds,
            onRecordIdsChange: setExpandedIds
          },
          content: (record) => (
            <DataTable
              noHeader={true}
              defaultColumnProps={{
                cellsSx: {fontStyle: 'italic', backgroundColor: "#f1f3f5"},
              }}
              columns={[
                {
                  accessor: "type",
                  width: 100,
                  sortable: true,
                  render: (record) => undefined,
                },
                {
                  accessor: "fromDttm",
                  width: 100,
                  sortable: true,
                  render: (record) => undefined,
                },
                {
                  accessor: "operatorUnitId",
                  width: 100,
                  sortable: true,
                  render: (record) => undefined,
                },
                {
                  accessor: "truckCompanyId",
                  width: 100,
                  sortable: true,
                  render: ({truckCompanyName}) => {
                    return <DefaultText value={truckCompanyName}/>
                  },
                },
                {
                  accessor: "status.code",
                  width: 100,
                  sortable: true,
                  render: ({currentStatus}) => (
                    <FormattedActivityPeriodStatus value={currentStatus} iconFill={false}/>
                  ),
                },
                {
                  accessor: "cost",
                  width: 100,
                  sortable: true,
                  render: ({cost}) => (
                    <FormattedCurrency value={cost}/>
                  ),
                },
                {
                  accessor: "qty",
                  width: 100,
                  sortable: true,
                  render: ({qty}) => (
                    <FormattedQuantity value={{number: qty}}/>
                  ),
                },
                {
                  accessor: "ticketQty",
                  width: 100,
                  sortable: true,
                  render: ({ticketQty}) => (
                    <FormattedQuantity value={{number: ticketQty}} options={{maximumFractionDigits: 0}}/>
                  ),
                },
                {
                  accessor: "",
                  width: 100,
                  sortable: true,
                  render: (record) => {
                    if (record.activityPeriodReports) {
                      return (
                        <Group spacing={4} position="right" noWrap>
                          <ActionIcon onClick={(e) => {
                            e.stopPropagation();
                            setSelectedActivityPeriod(record)
                          }}>
                            <IconFolders size={16}/>
                          </ActionIcon>
                        </Group>
                      )
                    } else {
                      return undefined
                    }
                  }
                },
              ]}
              records={activityPeriodLookup[record.record.id]?.sort(getStringSorter('truckCompanyName')) ?? []}
            />
          ),
        }}
      />
      {selectedActivityPeriod &&
        <ActivityPeriodFileTableModal
          size="xl"
          opened={true}
          onClose={() => setSelectedActivityPeriod(undefined)}
          scrollAreaComponent={ScrollArea.Autosize}
          activityPeriod={selectedActivityPeriod}/>
      }
    </>
  );
};
